import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Stored shape: { decision: 'all' | 'necessary', analytics: boolean, version: string, at: number }
const STORAGE_KEY = 'glimt_cookie_consent_v1';
const CONSENT_VERSION = '1.0';

const CookieConsentContext = createContext({
  consent: null,
  hasDecided: false,
  acceptAll: () => {},
  acceptNecessary: () => {},
  reopen: () => {},
});

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.decision === 'all' || parsed.decision === 'necessary')) {
      return parsed;
    }
  } catch (_) {}
  return null;
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(() => readStored());

  // Hook point for future analytics: only initialise tracking when the user
  // has actively opted in. Today the app loads no analytics, so this is a
  // no-op placeholder that keeps the gate in one place.
  useEffect(() => {
    if (consent?.analytics) {
      // e.g. import('firebase/analytics').then(({ getAnalytics }) => getAnalytics(app));
    }
  }, [consent?.analytics]);

  const persist = (next) => {
    const value = { ...next, version: CONSENT_VERSION, at: Date.now() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch (_) {}
    setConsent(value);
  };

  const acceptAll = useCallback(() => persist({ decision: 'all', analytics: true }), []);
  const acceptNecessary = useCallback(() => persist({ decision: 'necessary', analytics: false }), []);
  const reopen = useCallback(() => setConsent(null), []);

  return (
    <CookieConsentContext.Provider
      value={{
        consent,
        hasDecided: !!consent,
        acceptAll,
        acceptNecessary,
        reopen,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
